import { ReactNode, useEffect, useMemo } from 'react'
import { AtendimentoType, initialQueryState, OptionsHelper, useDebounce } from '_metronic/helpers'
import {
  ActiveFilter,
  ActiveFiltersBar,
  CustomSelect,
  IOption,
  TableFilter,
  TableFilters,
} from 'app/components'
import { useQueryRequest } from '../../core/QueryRequestProvider'
import { useQueryResponse } from '../../core/QueryResponseProvider'
import { useListFilters } from '../../core/ListFiltersProvider'

type Props = {
  /** Conteúdo à direita da linha dos campos (ex.: "Novo valor"). */
  actions?: ReactNode
}

export const EmpresaValoresListSearchComponent = ({ actions }: Props) => {
  const { updateState } = useQueryRequest()
  const { response, isLoading } = useQueryResponse()
  const { filters, setFilter, clearFilters } = useListFilters()
  const total = response?.totalElements ?? 0

  const debouncedFilters = useDebounce(filters, 500)

  const search = useMemo(
    () =>
      [
        debouncedFilters.tipoAtendimento ? `tipo:${debouncedFilters.tipoAtendimento.value}` : '',
        debouncedFilters.especialidade
          ? `especialidade:${debouncedFilters.especialidade.value}`
          : '',
      ]
        .filter(Boolean)
        .join(','),
    [debouncedFilters]
  )

  useEffect(() => {
    updateState({ ...initialQueryState, search })
  }, [search, updateState])

  // Chips da faixa "Filtros ativos": cada × limpa só o seu campo.
  const activeFilters: ActiveFilter[] = [
    filters.tipoAtendimento && {
      key: 'tipoAtendimento',
      label: 'Tipo',
      value: filters.tipoAtendimento.label,
      onRemove: () => setFilter('tipoAtendimento', undefined),
    },
    filters.especialidade && {
      key: 'especialidade',
      label: 'Especialidade',
      value: filters.especialidade.label,
      onRemove: () => setFilter('especialidade', undefined),
    },
  ].filter((filter): filter is ActiveFilter => Boolean(filter))

  return (
    <TableFilters
      actions={actions}
      footer={
        <ActiveFiltersBar
          filters={activeFilters}
          onClearAll={clearFilters}
          summary={
            isLoading
              ? 'Buscando…'
              : `${total} ${total === 1 ? 'valor encontrado' : 'valores encontrados'}`
          }
        />
      }
    >
      <TableFilter label='Tipo de atendimento'>
        <CustomSelect
          placeholder='Selecione um tipo'
          onChange={(tipo?: IOption<AtendimentoType>) =>
            setFilter('tipoAtendimento', tipo ?? undefined)
          }
          fetchOptions={OptionsHelper.getOptionsTiposAtendimento}
          value={filters.tipoAtendimento}
        />
      </TableFilter>
      <TableFilter label='Especialidade'>
        <CustomSelect
          placeholder='Selecione uma especialidade'
          onChange={(especialidade?: IOption<number>) =>
            setFilter('especialidade', especialidade ?? undefined)
          }
          fetchOptions={OptionsHelper.getOptionsEspecialidades}
          value={filters.especialidade}
        />
      </TableFilter>
    </TableFilters>
  )
}
